import { isEmpty } from 'lodash';
import { isFree, isRentEnabled, isSaleEnabled, isBought, isRented } from '../../common/ui-selectors/video/pricing';
import { isSubscribedTo, isSubscriptionEnabled, isPricingPlanEnabled, hasAccessToChannelByPricingPlan } from '../../common/ui-selectors/channel/pricing';
import { PREVIEW_TYPES } from '@wix/wix-vod-constants/common';
import { hasTrailer, getPreviewType } from '../../common/ui-selectors/video/preview';
import { canAccessChannelByPricingPlan, canAccessChannelWithSubscription } from './channel-access';
import { isReadyLiveStream, isPendingLiveStream, isScheduledLive } from '../../common/ui-selectors/video/live';
import { isExternalVideo } from '../../common/ui-selectors/video/video-origin';
import { getPricingPlanInfo, getSubscriptionPriceInfo } from '../../common/utils/deal-info-helpers';
function isLoggedIn(currentSiteUser) {
  return !isEmpty(currentSiteUser);
}
export function isForMemberOnly(video) {
  return Boolean(video.memberOnly);
}
export function isVideoExist(video) {
  return !isScheduledLive(video) && !isPendingLiveStream(video);
}
export function isAccessAllowed(channel, video, currentSiteUser) {
  if (isFree(video)) {
    if (isSubscriptionEnabled(channel) && !isExternalVideo(video)) {
      return isSubscribedTo(channel);
    } else if (isPricingPlanEnabled(channel) && !isExternalVideo(video)) {
      return hasAccessToChannelByPricingPlan(channel);
    } else {
      return !isForMemberOnly(video) || isLoggedIn(currentSiteUser);
    }
  }
  return isRented(video) || isBought(video) || canAccessChannelWithSubscription(channel) || canAccessChannelByPricingPlan(channel);
  /* eslint-enable */
}
export function isDownloadAllowed(channel, video, currentSiteUser) {
  if (isVideoExist(video)) {
    if (isFree(video)) {
      return !isForMemberOnly(video) || isLoggedIn(currentSiteUser);
    } else {
      return isBought(video) || canAccessChannelWithSubscription(channel) || canAccessChannelByPricingPlan(channel);
    }
  }
  return false;
}
export function canAccessFullVideo(channel, video, currentSiteUser) {
  return isVideoExist(video) && isAccessAllowed(channel, video, currentSiteUser);
}

// What user need to do to get access to full video
export const FULL_ACCESS_ACTION_NAMES = {
  SIGN_IN: 'needs-sign-in',
  SUBSCRIBE: 'needs-subscription',
  BUY: 'need-to-buy',
  RENT: 'need-to-rent',
  BUY_OR_RENT: 'need-to-buy-or-rent',
  PRICING_PLAN: 'pricing-plan-enabled'
};
export function getFullAccessActionName(channel, video, currentSiteUser) {
  if (canAccessFullVideo(channel, video, currentSiteUser)) {
    return null;
  }
  if (isAccessAllowed(channel, video, currentSiteUser) && (isScheduledLive(video) || isPendingLiveStream(video))) {
    return null;
  }
  if (isPricingPlanEnabled(channel)) {
    const pricingPlanInfo = getPricingPlanInfo(channel.dealInfo);
    // channel.subscriptionOnly shows if purchase or rent of single videos in a channel is disabled.
    // this value common for subscription and pricing plans.
    // channel.subscriptionOnly - in V2, pricingPlanInfo?.required - in V3
    if (isFree(video) || channel.subscriptionOnly || pricingPlanInfo != null && pricingPlanInfo.required) {
      return FULL_ACCESS_ACTION_NAMES.PRICING_PLAN;
    }
  }
  if (isFree(video) && isSubscriptionEnabled(channel)) {
    return isSubscribedTo(channel) ? null : FULL_ACCESS_ACTION_NAMES.SUBSCRIBE;
  }
  if (isFree(video) && isForMemberOnly(video)) {
    return FULL_ACCESS_ACTION_NAMES.SIGN_IN;
  }
  if (isSubscriptionEnabled(channel)) {
    const subscriptionInfo = getSubscriptionPriceInfo(channel.dealInfo);
    // channel.subscriptionOnly shows if purchase or rent of single videos in a channel is disabled.
    // this value common for subscription and pricing plans.
    // channel.subscriptionOnly - in V2, subscriptionInfo?.required - in V3
    if (channel.subscriptionOnly || subscriptionInfo != null && subscriptionInfo.required) {
      return FULL_ACCESS_ACTION_NAMES.SUBSCRIBE;
    }
  }
  if (isRentEnabled(video) && isSaleEnabled(video)) {
    return FULL_ACCESS_ACTION_NAMES.BUY_OR_RENT;
  }
  if (isRentEnabled(video)) {
    return FULL_ACCESS_ACTION_NAMES.RENT;
  }
  if (isSaleEnabled(video)) {
    return FULL_ACCESS_ACTION_NAMES.BUY;
  }
}
export let AVAILABLE_VIDEO_TYPES = /*#__PURE__*/function (AVAILABLE_VIDEO_TYPES) {
  AVAILABLE_VIDEO_TYPES["FULL"] = "full-video";
  AVAILABLE_VIDEO_TYPES["TRAILER"] = "trailer";
  AVAILABLE_VIDEO_TYPES["FIRST_20_SEC"] = "preview";
  AVAILABLE_VIDEO_TYPES["LIVE_STREAMING"] = "live-streaming";
  return AVAILABLE_VIDEO_TYPES;
}({});
export function getAvailableVideoType(channel, video, currentSiteUser) {
  if (canAccessFullVideo(channel, video, currentSiteUser)) {
    return isReadyLiveStream(video) ? AVAILABLE_VIDEO_TYPES.LIVE_STREAMING : AVAILABLE_VIDEO_TYPES.FULL;
  }
  if (isFree(video) && isForMemberOnly(video)) {
    return;
  }
  const previewType = getPreviewType(video);
  if (hasTrailer(video) && previewType === PREVIEW_TYPES.TRAILER) {
    return AVAILABLE_VIDEO_TYPES.TRAILER;
  }
  if (previewType === PREVIEW_TYPES.FIRST_20_SEC) {
    return AVAILABLE_VIDEO_TYPES.FIRST_20_SEC;
  }
}
export function canPlayFullOrPreview(channel, video, currentSiteUser) {
  return Boolean(getAvailableVideoType(channel, video, currentSiteUser));
}
export let OWNERSHIP_STATUSES = /*#__PURE__*/function (OWNERSHIP_STATUSES) {
  OWNERSHIP_STATUSES["YOU_BOUGHT_THIS"] = "user-bought-video";
  OWNERSHIP_STATUSES["YOU_RENTED_THIS"] = "user-rented-video";
  return OWNERSHIP_STATUSES;
}({});
export const getOwnershipStatus = video => {
  if (isBought(video)) {
    return OWNERSHIP_STATUSES.YOU_BOUGHT_THIS;
  }
  if (isRented(video)) {
    return OWNERSHIP_STATUSES.YOU_RENTED_THIS;
  }
};
export const getAvailabilityStatus = _ref => {
  let {
    channelData,
    videoItem,
    currentSiteUser
  } = _ref;
  return getOwnershipStatus(videoItem) || getFullAccessActionName(channelData, videoItem, currentSiteUser);
};
//# sourceMappingURL=video-access.js.map